import { useState } from 'react';

// Components
import { PillTabs, TextBlock, SMPFlex, ConfigContext, SizeProvider } from '@velinsi/smp-widgets-ui';
import GroupSection from '../../components/GroupSection';

// Hooks
import useMatchesH2H from '../../hooks/useMatchesH2H';
import useGroupedMatchesH2H from '../../hooks/useGroupedMatchesH2H';

// Normalizers
import { normalizeFootballMatchesH2HResponseData } from '../../normalizers/normalizeFootballMatchesH2HResponseData';
import { normalizeFootballTeams } from '../../normalizers/normalizeFootballTeams';

// Utils
import { defaultHandleEventClick, defaultHandleTournamentClick } from '@sportal365/smp-widget-utils';

// Constants
import { Sports } from '../../../../../packages/constants/sportType';

// Types
import { ITournament, BaseWidgetProps, IMatchesH2HConfig } from '../../../../../packages/types';

// Misc
import ThemeProvider from '../../../../../packages/theme/ThemeProvider';
import useRootNodeRef from '../../../../../packages/hooks/useRootNodeRef';

export enum H2HFilters {
  H2H = 'H2H',
  FIRST_TEAM = 'FIRST_TEAM',
  SECOND_TEAM = 'SECOND_TEAM',
}

export const MatchesH2HWidget = ({ themes, config, subscribers, shadow, sdkOptions, cuid }: BaseWidgetProps<IMatchesH2HConfig>) => {
  const [selectedFilter, setSelectedFilter] = useState(config?.dataHeaderDefaultOption ?? H2HFilters.H2H);
  const { ref } = useRootNodeRef();

  const {
    showWidget,
    handleClick,
    widgetData: { matchesGroupedByTournament, teams, areLoadingGroupedMatches },
  } = useGroupedMatchesH2H({
    subscribers,
    dataWidgetId: config?.dataWidgetId,
    dataWidgetSport: config?.dataWidgetSport,
    dataRefreshTime: config?.dataRefreshTime,
    dataSportEntityOne: config?.dataSportEntityOne,
    dataSportEntityTwo: config?.dataSportEntityTwo,
    dataLimit: config?.dataLimit,
    filter: selectedFilter,
    dataWidgetCuid: cuid,
  });

  const getSelectedTeamId = () => {
    if (selectedFilter === H2HFilters.FIRST_TEAM && teams) {
      return String(teams?.firstTeam?.id) ?? '';
    }

    if (selectedFilter === H2HFilters.SECOND_TEAM && teams) {
      return String(teams?.secondTeam?.id) ?? '';
    }

    return undefined;
  };

  const {
    widgetData: { matches, areLoadingMatches },
  } = useMatchesH2H({
    subscribers,
    dataWidgetId: config?.dataWidgetId,
    dataWidgetSport: config?.dataWidgetSport,
    dataRefreshTime: config?.dataRefreshTime,
    filter: selectedFilter,
    dataLimit: config?.dataLimit,
    teamId: getSelectedTeamId(),
  });

  if (!showWidget) return null;

  const data = normalizeFootballMatchesH2HResponseData({
    matchesGroupedByTournament,
    matches,
    dataEntityLinks: config?.dataEntityLinks,
    dataTeamNameType: config?.dataTeamNameType,
    dataLabels: config?.dataLabels,
    filter: selectedFilter,
    teamId: getSelectedTeamId(),
  });

  const normalizedTeams = normalizeFootballTeams({
    teams,
    dataEntityLinks: config?.dataEntityLinks,
    dataTeamNameType: config?.dataTeamNameType,
  });

  const handleTournamentClick = (tournament: ITournament) => defaultHandleTournamentClick(handleClick, tournament.id, tournament.name);

  const handleEventClick = (event: string | number) => defaultHandleEventClick(handleClick, Number(event));

  const filters = [
    { name: config?.dataLabels?.H2H ?? H2HFilters.H2H, value: H2HFilters.H2H },
    { name: normalizedTeams?.firstTeam?.name ?? '', value: H2HFilters.FIRST_TEAM },
    { name: normalizedTeams?.secondTeam?.name ?? '', value: H2HFilters.SECOND_TEAM },
  ];

  return (
    <ConfigContext.Provider value={{ ...config, sdkOptions }}>
      <ThemeProvider
        isLoading={selectedFilter === H2HFilters.H2H ? areLoadingGroupedMatches : areLoadingMatches}
        shadow={shadow}
        themes={themes}
        dataTheme={config?.dataTheme}
        css={{ backgroundColor: '$surfaceBackgroundColor' }}
        refForShadow={ref}
      >
        <SizeProvider>
          {config?.dataOptionsDisplay && (
            <SMPFlex variant={selectedFilter === H2HFilters.H2H ? 'standingsFilters' : 'footballMatchesH2HFilters'}>
              <PillTabs
                tabs={filters}
                selectedTab={selectedFilter}
                handleTabClick={setSelectedFilter}
                handleSubscriptionClick={handleClick}
              />
            </SMPFlex>
          )}
          {data ? (
            data?.map(({ tournament, matches }, index) => {
              return (
                <GroupSection
                  handleClick={handleClick}
                  handleEventClick={handleEventClick}
                  sport={Sports.FOOTBALL}
                  key={`${tournament?.id}-${index}`}
                  matches={matches}
                  tournament={tournament}
                  handleTournamentClick={handleTournamentClick}
                  showTeamWinnerForm={selectedFilter !== H2HFilters.H2H}
                  selectedTeamId={getSelectedTeamId()}
                  allowInlineOdds={false}
                  oneLine={false}
                />
              );
            })
          ) : (
            <TextBlock text={config?.dataLabels?.no_games} />
          )}
        </SizeProvider>
      </ThemeProvider>
    </ConfigContext.Provider>
  );
};
